// hooks/usePasswordValidation.js
import { useState, useEffect, useCallback } from 'react';
import { 
  validatePassword, 
  validatePasswordMatch, 
  validatePasswordWithConfig,
  VALIDATION_MESSAGES 
} from '../utils/passwordValidation';

/**
 * パスワードバリデーション用カスタムフック
 * @param {Object} options - オプション設定
 * @param {boolean} options.validateOnChange - 入力時にリアルタイムバリデーションするかどうか（デフォルト: true）
 * @param {boolean} options.required - パスワードが必須かどうか（デフォルト: false）
 * @param {boolean} options.showErrorsImmediately - 入力開始と同時にエラーを表示するかどうか（デフォルト: false）
 * @param {Object} options.validationConfig - カスタムバリデーション設定
 * @returns {Object} バリデーション結果とヘルパー関数
 */
export const usePasswordValidation = (options = {}) => {
  const {
    validateOnChange = true,
    required = false,
    showErrorsImmediately = false,
    validationConfig = null
  } = options;

  // 基本状態
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  
  // タッチ状態（ユーザーがフィールドに触れたかどうか）
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });

  // 入力開始状態（何か文字が入力されたかどうか）
  const [hasStartedTyping, setHasStartedTyping] = useState({
    password: false,
    confirmPassword: false
  });

  // ローディング状態
  const [isValidating, setIsValidating] = useState(false);

  // パスワードバリデーション実行
  const runPasswordValidation = useCallback((pwd) => {
    setIsValidating(true);
    
    let errors = [];
    
    // カスタム設定がある場合はそれを使用、そうでなければデフォルト
    if (validationConfig) {
      errors = validatePasswordWithConfig(pwd, validationConfig);
    } else {
      errors = validatePassword(pwd);
    }
    
    // 必須チェック
    if (required && !pwd) {
      errors.unshift(VALIDATION_MESSAGES.PASSWORD_REQUIRED);
    }
    
    setPasswordErrors(errors);
    setIsPasswordValid(errors.length === 0 && (!required || pwd));
    
    setIsValidating(false);
  }, [required, validationConfig]);

  // パスワード一致チェック
  const checkPasswordMatch = useCallback((pwd, confirmPwd) => {
    const match = validatePasswordMatch(pwd, confirmPwd);
    setIsPasswordMatch(match);
    return match;
  }, []);

  // パスワード変更ハンドラー
  const handlePasswordChange = useCallback((value) => {
    setPassword(value);
    
    // 入力開始状態を更新
    if (value && !hasStartedTyping.password) {
      setHasStartedTyping(prev => ({ ...prev, password: true }));
    }
    
    if (validateOnChange || touched.password || (showErrorsImmediately && hasStartedTyping.password)) {
      runPasswordValidation(value);
    }
    
    // 確認パスワードとの一致チェック
    if (confirmPassword && (validateOnChange || touched.confirmPassword || (showErrorsImmediately && hasStartedTyping.confirmPassword))) {
      checkPasswordMatch(value, confirmPassword);
    }
  }, [validateOnChange, touched.password, touched.confirmPassword, confirmPassword, runPasswordValidation, checkPasswordMatch, showErrorsImmediately, hasStartedTyping.password, hasStartedTyping.confirmPassword]);

  // 確認パスワード変更ハンドラー
  const handleConfirmPasswordChange = useCallback((value) => {
    setConfirmPassword(value);
    
    // 入力開始状態を更新
    if (value && !hasStartedTyping.confirmPassword) {
      setHasStartedTyping(prev => ({ ...prev, confirmPassword: true }));
    }
    
    if (validateOnChange || touched.confirmPassword || (showErrorsImmediately && hasStartedTyping.confirmPassword)) {
      checkPasswordMatch(password, value);
    }
  }, [validateOnChange, touched.confirmPassword, password, checkPasswordMatch, showErrorsImmediately, hasStartedTyping.confirmPassword]);

  // フィールドのタッチ状態更新
  const handlePasswordBlur = useCallback(() => {
    setTouched(prev => ({ ...prev, password: true }));
    if (!validateOnChange && !showErrorsImmediately) {
      runPasswordValidation(password);
    }
  }, [validateOnChange, showErrorsImmediately, password, runPasswordValidation]);

  const handleConfirmPasswordBlur = useCallback(() => {
    setTouched(prev => ({ ...prev, confirmPassword: true }));
    if (!validateOnChange && !showErrorsImmediately) {
      checkPasswordMatch(password, confirmPassword);
    }
  }, [validateOnChange, showErrorsImmediately, password, confirmPassword, checkPasswordMatch]);

  // フォーカス時のハンドラー
  const handlePasswordFocus = useCallback(() => {
    // フォーカス時に何かしたい場合（例：ヒント表示など）
  }, []);

  const handleConfirmPasswordFocus = useCallback(() => {
    // フォーカス時に何かしたい場合
  }, []);

  // フォーム送信時の最終バリデーション
  const validateForm = useCallback(() => {
    // すべてのフィールドをタッチ状態にする
    setTouched({ password: true, confirmPassword: true });
    
    // パスワードバリデーション
    runPasswordValidation(password);
    
    // 確認パスワードチェック
    const match = checkPasswordMatch(password, confirmPassword);
    
    let errors = [];
    if (validationConfig) {
      errors = validatePasswordWithConfig(password, validationConfig);
    } else {
      errors = validatePassword(password);
    }
    
    if (required && !password) {
      errors.unshift(VALIDATION_MESSAGES.PASSWORD_REQUIRED);
    }
    
    const isValid = errors.length === 0 && match && (!required || password);
    
    return {
      isValid,
      errors: {
        password: errors,
        confirmPassword: match ? [] : [VALIDATION_MESSAGES.PASSWORD_MISMATCH]
      }
    };
  }, [password, confirmPassword, required, runPasswordValidation, checkPasswordMatch, validationConfig]);

  // パスワードリセット
  const resetPassword = useCallback(() => {
    setPassword('');
    setConfirmPassword('');
    setPasswordErrors([]);
    setIsPasswordValid(false);
    setIsPasswordMatch(true);
    setTouched({ password: false, confirmPassword: false });
    setHasStartedTyping({ password: false, confirmPassword: false });
  }, []);

  // パスワード値の直接設定（外部から値を設定する場合）
  const setPasswordValue = useCallback((pwd, confirmPwd = '') => {
    setPassword(pwd);
    setConfirmPassword(confirmPwd);
    
    // 値が設定された場合は入力開始状態にする
    if (pwd) {
      setHasStartedTyping(prev => ({ ...prev, password: true }));
    }
    if (confirmPwd) {
      setHasStartedTyping(prev => ({ ...prev, confirmPassword: true }));
    }
    
    if (pwd || required) {
      runPasswordValidation(pwd);
    }
    
    if (confirmPwd) {
      checkPasswordMatch(pwd, confirmPwd);
    }
  }, [required, runPasswordValidation, checkPasswordMatch]);

  // 入力されたパスワードの基本統計を取得
  const getPasswordStats = useCallback(() => {
    if (!password) return null;
    
    return {
      length: password.length,
      uniqueChars: new Set(password).size
    };
  }, [password]);

  // デバウンス機能付きバリデーション
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  const debouncedValidation = useCallback((pwd, delay = 300) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      runPasswordValidation(pwd);
    }, delay);
    
    setDebounceTimer(timer);
  }, [debounceTimer, runPasswordValidation]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // エラー表示条件を決定
  const shouldShowPasswordErrors = showErrorsImmediately 
    ? hasStartedTyping.password && passwordErrors.length > 0
    : touched.password && passwordErrors.length > 0;

  const shouldShowPasswordMatchError = showErrorsImmediately
    ? hasStartedTyping.confirmPassword && !isPasswordMatch
    : touched.confirmPassword && !isPasswordMatch;

  return {
    // 基本状態
    password,
    confirmPassword,
    passwordErrors,
    isPasswordValid,
    isPasswordMatch,
    touched,
    hasStartedTyping,
    isValidating,
    
    // 基本ハンドラー
    handlePasswordChange,
    handleConfirmPasswordChange,
    handlePasswordBlur,
    handleConfirmPasswordBlur,
    handlePasswordFocus,
    handleConfirmPasswordFocus,
    
    // ユーティリティ関数
    validateForm,
    resetPassword,
    setPasswordValue,
    debouncedValidation,
    
    // 計算値・状態チェック
    hasPasswordErrors: passwordErrors.length > 0,
    showPasswordErrors: shouldShowPasswordErrors,
    showPasswordMatchError: shouldShowPasswordMatchError,
    isFormValid: isPasswordValid && isPasswordMatch,
    passwordStats: getPasswordStats(),
    isPasswordSecure: () => isPasswordValid,
    
    // 設定情報
    config: {
      validateOnChange,
      required,
      showErrorsImmediately,
      validationConfig
    }
  };
};

/**
 * 簡易版パスワードバリデーションフック（最小限の機能）
 * @param {boolean} required - パスワードが必須かどうか
 * @returns {Object} 基本的なバリデーション結果
 */
export const useSimplePasswordValidation = (required = false) => {
  return usePasswordValidation({
    validateOnChange: true,
    required,
    showErrorsImmediately: false
  });
};

/**
 * 高度なパスワードバリデーションフック（全機能有効）
 * @param {Object} config - 設定オブジェクト
 * @returns {Object} 全機能付きバリデーション結果
 */
export const useAdvancedPasswordValidation = (config = {}) => {
  return usePasswordValidation({
    validateOnChange: true,
    required: true,
    showErrorsImmediately: false,
    ...config
  });
};

/**
 * リアルタイムエラー表示対応パスワードバリデーションフック
 * @param {Object} config - 設定オブジェクト
 * @returns {Object} リアルタイムエラー表示付きバリデーション結果
 */
export const useRealtimePasswordValidation = (config = {}) => {
  return usePasswordValidation({
    validateOnChange: true,
    required: false,
    showErrorsImmediately: true,
    ...config
  });
};

/**
 * 3種類文字要件対応パスワードバリデーションフック
 * @param {Object} config - 設定オブジェクト
 * @returns {Object} 3種類文字要件に特化したバリデーション結果
 */
export const useThreeTypePasswordValidation = (config = {}) => {
  return usePasswordValidation({
    validateOnChange: true,
    required: true,
    showErrorsImmediately: false,
    ...config
  });
};